import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { AppDispatch, getVideoProvider, updateVideoProvider } from "../store";
import {
  VIDEO_PROVIDERS,
  VideoProvider as VideoProviderType,
  resolveVideoProvider,
} from "../constants/videoProviders";
import { CustomCloseButton } from "../components/ToasterCloseButton";

const TOAST_OPTIONS = {
  autoClose: 3000,
  closeButton: <CustomCloseButton />,
  closeOnClick: false,
  icon: () => null,
};

const VideoProvider: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // `provider` is the persisted truth returned by the API. It is only ever
  // updated after a successful write so a failed update cannot leave the
  // page showing a value that was never actually saved (AC #7).
  const [provider, setProvider] = useState<VideoProviderType | null>(null);
  const [pending, setPending] = useState<VideoProviderType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchProvider = async () => {
      setIsLoading(true);
      const res = await dispatch(getVideoProvider());
      if (cancelled) {
        return;
      }
      if (res.meta.requestStatus === "fulfilled") {
        // Normalise: an absent/null/unknown value resolves to jitsi, matching
        // the server's own FR4a default, rather than blanking the control.
        setProvider(resolveVideoProvider(res.payload));
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
        toast.error("Failed to load the current video provider", TOAST_OPTIONS);
      }
      setIsLoading(false);
    };
    fetchProvider();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const openConfirm = (candidate: VideoProviderType) => {
    if (candidate === provider) {
      return;
    }
    setPending(candidate);
  };

  const closeConfirm = () => {
    if (isUpdating) {
      return;
    }
    setPending(null);
  };

  // Let keyboard users dismiss the confirmation with Escape (unless a write is
  // in flight), mirroring the overlay-click cancel path.
  useEffect(() => {
    if (!pending) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeConfirm();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, isUpdating]);

  const onConfirm = async () => {
    if (!pending) {
      return;
    }
    setIsUpdating(true);
    const res = await dispatch(updateVideoProvider({ videoProvider: pending }));
    if (res.meta.requestStatus === "fulfilled") {
      // Set to the value the API confirms, normalised, not the value we sent.
      setProvider(resolveVideoProvider(res.payload));
      toast.success("Video provider updated", TOAST_OPTIONS);
    } else {
      toast.error(
        res.payload?.error || "Failed to change the video provider",
        TOAST_OPTIONS
      );
      // `provider` is left untouched here on purpose: the page must keep
      // showing the last known-persisted value, not the attempted one.
    }
    setIsUpdating(false);
    setPending(null);
  };

  return (
    <PageContainer>
      <Title>Global Video Provider</Title>
      {isLoading ? (
        <LoadingText>Loading current provider...</LoadingText>
      ) : loadFailed ? (
        <LoadingText>
          Could not load the current video provider. Reload the page to try again.
        </LoadingText>
      ) : (
        <CurrentProviderBox>
          <CurrentProviderLabel>Currently active:</CurrentProviderLabel>
          <CurrentProviderValue data-testid="current-provider">
            {provider}
          </CurrentProviderValue>
        </CurrentProviderBox>
      )}

      <ControlRow>
        {VIDEO_PROVIDERS.map((option) => (
          <ProviderButton
            key={option}
            type="button"
            $isActive={option === provider}
            aria-pressed={option === provider}
            disabled={isLoading || loadFailed || option === provider}
            onClick={() => openConfirm(option)}
          >
            {option}
          </ProviderButton>
        ))}
      </ControlRow>

      {pending && (
        <PopupOverlay onClick={closeConfirm}>
          <PopupContainer
            role="dialog"
            aria-modal="true"
            aria-labelledby="video-provider-confirm-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <PopupDescription id="video-provider-confirm-desc">
              Are you sure you want to switch the global video provider from{" "}
              <strong>{provider}</strong> to <strong>{pending}</strong>?
              <br />
              <br />
              This affects <strong>every session on the platform</strong>,
              including sessions already in progress. Participants currently
              in a call will be moved to the new provider.
            </PopupDescription>

            <ButtonGroup>
              <ConfirmButton
                type="button"
                onClick={onConfirm}
                disabled={isUpdating}
                aria-busy={isUpdating}
                autoFocus
              >
                {isUpdating ? (
                  <>
                    <LoadingSpinner />
                    <VisuallyHidden>Switching provider…</VisuallyHidden>
                  </>
                ) : (
                  "Yes, switch provider"
                )}
              </ConfirmButton>
              <CancelButton type="button" onClick={closeConfirm} disabled={isUpdating}>
                No
              </CancelButton>
            </ButtonGroup>
          </PopupContainer>
        </PopupOverlay>
      )}
    </PageContainer>
  );
};

export default VideoProvider;

const PageContainer = styled.div`
  padding: 40px;
  max-width: 600px;
`;

const Title = styled.h1`
  font-size: 24px;
  margin-bottom: 20px;
  color: ${(props) => props.theme.colors.secondary};
`;

const LoadingText = styled.p`
  color: ${(props) => props.theme.colors.grey};
`;

const CurrentProviderBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 24px;
  padding: 16px;
  background: ${(props) => props.theme.colors.light};
  border-radius: 8px;
`;

const CurrentProviderLabel = styled.span`
  color: ${(props) => props.theme.colors.greyText};
`;

const CurrentProviderValue = styled.span`
  font-weight: bold;
  text-transform: capitalize;
  color: ${(props) => props.theme.colors.primaryTextColor};
`;

const ControlRow = styled.div`
  display: flex;
  gap: 12px;
`;

const ProviderButton = styled.button<{ $isActive: boolean }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: 2px solid ${(props) => props.theme.colors.primary};
  background-color: ${(props) =>
    props.$isActive ? props.theme.colors.primary : "white"};
  color: ${(props) => (props.$isActive ? "white" : props.theme.colors.primary)};
  text-transform: capitalize;
  cursor: ${(props) => (props.$isActive ? "default" : "pointer")};
  opacity: ${(props) => (props.disabled && !props.$isActive ? 0.6 : 1)};

  &:hover {
    background-color: ${(props) =>
      props.$isActive ? props.theme.colors.primary : props.theme.colors.light};
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContainer = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PopupDescription = styled.p`
  text-align: left;
  margin-right: auto;
  font-weight: 400;
`;

// Off-screen text so screen-reader users get an announcement when the confirm
// button collapses to a spinner and loses its visible label.
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  gap: 10px;
  width: 100%;
`;

const ConfirmButton = styled.button`
  padding: 10px 20px;
  background-color: ${(props) => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: white;
  color: ${(props) => props.theme.colors.grey};
  border: 1px solid ${(props) => props.theme.colors.grey};
  border-radius: 8px;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
